import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useDocument } from '@nandorojo/swr-firestore';

import { getPathKey } from '@utils/getPath';
import CommentQuill from '@components/library/Quill/CommentQuill';
import CommentList from '@template/Comment/CommentList';
import ActionBarHeader from '../../actionBar/ActionBarHeader';
import { scrollbarStyle } from '@styles/variants/scrollbar';
import {
  ITimelineCommentSetting,
  defaultTimelineCommentSetting,
} from '@typings/setting/timelineSetting.interface';

const Wrapper = styled.div`
  width: 100%;
  height: calc(100% - 76px);
  display: grid;
  grid-template-rows: 1fr auto;
  overflow: hidden;
  background: var(--cardBackgroundColor);

  @media (max-width: 480px) {
    grid-template-rows: auto 1fr;
    max-height: calc(100dvh - 50px);
    position: fixed;
    bottom: 0;
    left: 0;
    box-shadow: 0px -8px 16px 0px rgba(0, 0, 0, 0.1);
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
  }
`;

const CommentWrap = styled.div`
  height: 100%;
  overflow: hidden;
  padding: 16px;
  ${scrollbarStyle}
`;

const AddCommentWrap = styled.div`
  width: 100%;
  padding: 16px;
  border-top: 1px solid var(--lineColor);
  overflow-y: auto;
  max-height: min(360px, 50dvh);

  @media (max-width: 480px) {
    max-height: min(280px, 48dvh);
  }
`;

function PostingCommentSec({
  docId,
  authorUid,
  showComment,
  close,
  commentCount,
}) {
  const collectionPath = getPathKey(1);

  // 📍 보드별 포스팅 댓글 설정을 동적 로드 (posting, posting2, posting3... 모두 지원)
  // Firestore 경로: setting/${collectionPath}_comment
  const { data: postingCommentSettingData } =
    useDocument<ITimelineCommentSetting>(`setting/${collectionPath}_comment`, {
      listen: true,
    });

  // 기본값과 병합
  const postingCommentSetting: ITimelineCommentSetting = useMemo(
    () => ({
      ...defaultTimelineCommentSetting,
      ...postingCommentSettingData,
    }),
    [postingCommentSettingData],
  );

  // CommentQuill에 전달할 commentSetting 구성
  const commentSetting = useMemo(
    () => ({
      secretCheckbox: postingCommentSetting.useSecret,
      dice: postingCommentSetting.useDice,
      battle: postingCommentSetting.useBattle,
      collapseComment: postingCommentSetting.useCollapseComment,
      luckyDraw: false, // 포스팅은 뽑기 제외
      roleplayComment: postingCommentSetting.useRoleplayComment,
      showCharacterCount: postingCommentSetting.showCharacterCount,
      maxCharacterCount: postingCommentSetting.maxCharacterCount,
      battleMode: postingCommentSetting.battleMode,
      battleIconConfigMap: postingCommentSetting.battleIconConfigMap,
    }),
    [postingCommentSetting],
  );

  // 툴바 설정
  const toolbar = useMemo(
    () => ({
      image: postingCommentSetting.useImage,
      video: postingCommentSetting.useVideo,
      emoji: postingCommentSetting.useEmoticon,
    }),
    [postingCommentSetting],
  );

  return (
    <Wrapper onClick={(e) => e.stopPropagation()}>
      <ActionBarHeader text={`댓글 ${commentCount}`} close={close} />
      <CommentWrap>
        <CommentList
          collectionPath={`${collectionPath}`}
          boardId={docId}
          boardAuthorUid={authorUid}
          wrapStyle={'transparent'}
          showReply
          boardCommentSetting={postingCommentSetting}
        />
      </CommentWrap>

      {showComment && (
        <AddCommentWrap>
          <CommentQuill
            boardId={docId}
            boardAuthorUid={authorUid || ''}
            placeholder="댓글 입력"
            setToolbar={toolbar}
            type="input"
            collectionPath={collectionPath}
            id={`posting-comment-${docId}`}
            useGlobalCommentSetting={false}
            commentSetting={commentSetting}
            maxHeight={140}
          />
        </AddCommentWrap>
      )}
    </Wrapper>
  );
}

export default React.memo(PostingCommentSec);
